import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import Footer from "../components/Footer";
import NavBar from "../components/NavBar";

const EFFECTIVE_DATE = "27 July 2026";

const policies = {
    acceptable: {
        path: "/acceptable-use-policy",
        label: "Acceptable Use Policy",
        title: "Acceptable Use Policy",
        intro: "This policy explains how SignetPrint may be used and the conduct we expect from businesses and customers using the platform.",
        sections: [
            {
                heading: "What SignetPrint provides",
                paragraphs: [
                    "SignetPrint provides software, directory, enquiry, and workflow tools for independent print businesses. SignetPrint is not the seller, manufacturer, fulfilment centre, courier, or merchant of record for printed products.",
                ],
            },
            {
                heading: "Permitted use",
                paragraphs: [
                    "You may use the platform to create or manage a legitimate printing-business listing, find printing services, submit a legitimate print enquiry, communicate about a print job, and use the platform tools made available to your account.",
                ],
            },
            {
                heading: "Prohibited use",
                paragraphs: [
                    "You must not use SignetPrint for unlawful, fraudulent, deceptive, abusive, or harmful activity; to impersonate a person or business; to upload content that infringes another person's rights; to distribute malware or unsolicited messages; to misuse personal information; or to offer goods or services that are prohibited by law.",
                    "You must not submit a print request unless you have the right to use the content and it can lawfully be reproduced. Users are responsible for the content they upload or submit.",
                ],
            },
            {
                heading: "Business listings and enforcement",
                paragraphs: [
                    "Print businesses must provide accurate business and contact information and keep their listing reasonably current. We may review, suspend, remove, or restrict an account or listing where we reasonably believe this policy, another platform rule, or applicable law has been breached.",
                ],
            },
            {
                heading: "Contact",
                paragraphs: [
                    "Questions or reports about misuse can be sent to shiv@signetsolutions.co.za.",
                ],
            },
        ],
    },
    shipping: {
        path: "/shipping-policy",
        label: "Shipping Policy",
        title: "Shipping Policy",
        intro: "SignetPrint does not sell, store, package, or ship printed products. Physical orders are fulfilled directly by the independent print business selected by the customer.",
        sections: [
            {
                heading: "Who fulfils the order",
                paragraphs: [
                    "The print business that accepts a job is responsible for producing it and for agreeing delivery or collection arrangements with the customer. That business is responsible for communicating its available delivery areas, fees, estimated times, and collection options.",
                ],
            },
            {
                heading: "Delivery and collection",
                paragraphs: [
                    "Customers should confirm delivery or collection details, timing, and any delivery charge directly with the print business before the job is accepted. SignetPrint does not receive or hold shipping payments and does not guarantee a third-party delivery time.",
                ],
            },
            {
                heading: "Delays or delivery problems",
                paragraphs: [
                    "For a late, damaged, incomplete, or misdelivered order, the customer should first contact the print business that accepted the job. SignetPrint can help route a platform complaint, but it is not the carrier or fulfilment provider.",
                ],
            },
            {
                heading: "Contact",
                paragraphs: [
                    "For platform-related assistance, contact shiv@signetsolutions.co.za. Product and delivery arrangements remain between the customer and the relevant print business.",
                ],
            },
        ],
    },
    returns: {
        path: "/returns-policy",
        label: "Returns and Refunds Policy",
        title: "Returns and Refunds Policy",
        intro: "SignetPrint is a software platform. It does not receive or hold payments for printed products and does not operate a product-return or product-refund account.",
        sections: [
            {
                heading: "Printed products and services",
                paragraphs: [
                    "A return, refund, cancellation, reprint, or quality issue for a printed product must be raised with the print business that accepted the job. The print business is responsible for its product terms and for meeting its obligations under applicable law.",
                    "Customers should contact the print business as soon as possible and provide the job details, the issue, and any relevant photographs or other evidence. Custom or made-to-order work may be subject to different return arrangements, but nothing in this policy removes a right that cannot lawfully be excluded.",
                ],
            },
            {
                heading: "SignetPrint subscriptions",
                paragraphs: [
                    "Print businesses pay SignetPrint a recurring monthly subscription for access to the platform. A business may request cancellation of future subscription renewals through its account or by contacting support. Any refund request for a SignetPrint subscription will be reviewed under the subscription terms and applicable law.",
                ],
            },
            {
                heading: "Disputes and platform complaints",
                paragraphs: [
                    "SignetPrint may help route a complaint or review a business's use of the platform. It does not adjudicate the private sale between a customer and a print business, hold the product payment, or issue a refund from a customer or vendor wallet.",
                ],
            },
            {
                heading: "Contact",
                paragraphs: [
                    "For platform support, contact shiv@signetsolutions.co.za. For a product return or product refund, contact the print business that accepted the job first.",
                ],
            },
        ],
    },
};

const PolicyLayout = ({ children, title, description }) => (
    <div className="min-h-screen bg-[#f8f8f6] font-['Plus_Jakarta_Sans',sans-serif] text-[#212326]">
        <Helmet>
            <title>{title} | SignetPrint</title>
            <meta name="description" content={description} />
        </Helmet>
        <NavBar />
        <main>{children}</main>
        <Footer />
    </div>
);

const PoliciesIndex = () => (
    <PolicyLayout
        title="Policies"
        description="SignetPrint platform policies for acceptable use, shipping, and returns."
    >
        <section className="px-5 py-16 md:px-10 md:py-24">
            <div className="mx-auto max-w-[920px]">
                <p className="mb-4 text-[12px] font-bold uppercase tracking-[1.8px] text-[#ff5323]">SignetPrint policies</p>
                <h1 className="max-w-[720px] text-4xl font-bold leading-tight md:text-6xl">Clear rules for a simple platform.</h1>
                <p className="mt-6 max-w-[680px] text-base leading-7 text-[#646a72] md:text-lg">
                    SignetPrint provides software and connections for independent print businesses. These policies explain our platform role and how print businesses handle physical orders with their customers.
                </p>
                <div className="mt-12 grid gap-5 md:grid-cols-3">
                    {Object.values(policies).map((policy) => (
                        <Link
                            key={policy.path}
                            to={policy.path}
                            className="rounded-[18px] border border-[#e4e5e7] bg-white p-6 no-underline shadow-[0_12px_30px_rgba(33,35,38,0.04)] transition-transform hover:-translate-y-1"
                        >
                            <h2 className="text-lg font-bold text-[#212326]">{policy.label}</h2>
                            <p className="mt-3 text-sm leading-6 text-[#646a72]">Read the policy</p>
                        </Link>
                    ))}
                </div>
                <p className="mt-10 text-sm leading-6 text-[#646a72]">
                    Effective date: {EFFECTIVE_DATE}. For questions about these policies, contact <a className="text-[#ff5323]" href="mailto:shiv@signetsolutions.co.za">shiv@signetsolutions.co.za</a>.
                </p>
            </div>
        </section>
    </PolicyLayout>
);

const PolicyPage = ({ policyKey }) => {
    const { policySlug } = useParams();
    const policy = policies[policyKey || policySlug];

    if (!policy) {
        return <PoliciesIndex />;
    }

    return (
        <PolicyLayout title={policy.title} description={policy.intro}>
            <section className="px-5 py-16 md:px-10 md:py-24">
                <article className="mx-auto max-w-[820px] rounded-[22px] border border-[#e4e5e7] bg-white px-6 py-8 shadow-[0_12px_30px_rgba(33,35,38,0.04)] md:px-12 md:py-12">
                    <Link to="/policies" className="text-sm font-bold text-[#ff5323] no-underline">Back to policies</Link>
                    <p className="mb-4 mt-10 text-[12px] font-bold uppercase tracking-[1.8px] text-[#ff5323]">SignetPrint policy</p>
                    <h1 className="text-4xl font-bold leading-tight md:text-5xl">{policy.title}</h1>
                    <p className="mt-6 border-b border-[#e4e5e7] pb-8 text-base leading-7 text-[#646a72]">{policy.intro}</p>
                    <div className="mt-8 space-y-8">
                        {policy.sections.map((section) => (
                            <section key={section.heading}>
                                <h2 className="text-xl font-bold">{section.heading}</h2>
                                {section.paragraphs.map((paragraph) => (
                                    <p key={paragraph} className="mt-3 text-[15px] leading-7 text-[#555b62]">{paragraph}</p>
                                ))}
                            </section>
                        ))}
                    </div>
                    <p className="mt-10 border-t border-[#e4e5e7] pt-6 text-sm text-[#646a72]">Effective date: {EFFECTIVE_DATE}</p>
                </article>
            </section>
        </PolicyLayout>
    );
};

export { PoliciesIndex };
export default PolicyPage;